import axios from 'axios';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,  // send axissec_session cookie on every request
});

/**
 * Attach a dev bearer token when no session cookie is present.
 *
 * Production path: the browser sends the HttpOnly `axissec_session` cookie
 * automatically (set by the Azure OAuth callback). No header needed.
 *
 * Dev fallback: if a JWT_SECRET-signed token is in localStorage under
 * `axissec_dev_token`, attach it as Authorization: Bearer. Useful when
 * Azure isn't configured (local dev) or for one-off API testing.
 */
api.interceptors.request.use((config) => {
  const devToken = localStorage.getItem('axissec_dev_token');
  if (devToken) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${devToken}`;
  }
  return config;
});

export interface ReconJobSummary {
  total_rows: number;
  match: number;
  qty_mismatch: number;
  only_in_portfolio: number;
  only_in_dp: number;
  isin_unresolvable: number;
  match_rate_pct: number;
  /** Live company-merged stats saved by the detail page (preferred by the list
   *  so it shows exactly what the user sees inside the job). */
  live_matched?: number | null;
  live_mismatches?: number | null;
  live_total?: number | null;
  live_match_rate_pct?: number | null;
  portfolio_warnings?: string[];
  /** Aggregate Claude spend for this job (sum across DP extraction + classifier calls).
   *  Present on every completed job for back-compat with older UI code. For
   *  the per-call drilldown, use cost_breakdown below — same totals,
   *  richer shape. */
  cost?: {
    total_usd: number;
    input_tokens: number;
    output_tokens: number;
    calls: number;
  };
  /** Structured cost breakdown — total split by purpose (reasoning =
   *  indexer pass; extraction = per-chunk extraction calls) plus a
   *  chronological list of every individual call with its label,
   *  model, latency, and cost. Used by the cost panel on JobDetailPage
   *  to render "$4.10 total · indexer $0.82 (2 calls) · extraction
   *  $3.28 (12 calls)" and a drilldown table. Absent on jobs that
   *  ran before commit 3 of the cost-optimization work; the UI falls
   *  back to the older `cost` shape when this field is missing. */
  cost_breakdown?: {
    total_usd: number;
    total_calls: number;
    total_input_tokens: number;
    total_output_tokens: number;
    by_purpose: {
      reasoning:  { calls: number; cost_usd: number; input_tokens: number; output_tokens: number };
      extraction: { calls: number; cost_usd: number; input_tokens: number; output_tokens: number };
    };
    calls: Array<{
      purpose: 'reasoning' | 'extraction';
      label: string;
      model: string;
      cost_usd: number;
      input_tokens: number;
      output_tokens: number;
      elapsed_ms: number;
    }>;
  };
  /** Present on transactions_v1 jobs only. Summarises the txn-level
   *  reconciliation. Holdings_v1 jobs leave this undefined. */
  txn_summary?: TxnReconSummary;
  /** Client ID extracted from the PF Excel header (e.g. 5226638). */
  pf_client_id?: string | null;
  /** Client ID extracted from the DP statement header. */
  dp_client_id?: string | null;
}

/** Per-job summary for transactions_v1 reconciliations. */
export interface TxnReconSummary {
  total_isins: number;
  total_portfolio_txns: number;
  total_dp_txns: number;
  total_matched_txns: number;
  total_unmatched_pf: number;
  total_unmatched_dp: number;
  total_outside_window: number;
  /** % of ISINs where portfolio_net == dp_net (within 0.001 tolerance). */
  txn_match_rate_pct: number;
  /** Net balance totals across all ISINs (net-balance recon mode). */
  total_portfolio_net?: number;
  total_dp_net?: number;
  total_difference?: number;
}

export interface ReconJob {
  id: string;
  status: 'queued' | 'processing' | 'awaiting_review' | 'completed' | 'failed' | 'cancelled';
  /** Which broker-export format this job ran against. Drives which view
   *  the UI shows: holdings-vs-holdings list (holdings_v1) or per-ISIN
   *  summary cards with txn drill-down (transactions_v1). */
  format_version?: 'holdings_v1' | 'transactions_v1';
  /** Optional. May be null on newer jobs where the user left the form
   *  field blank — the parser reads the as-on date from inside the
   *  Excel and DP PDF independently. */
  portfolio_as_on: string | null;
  dp_as_on: string | null;
  summary: ReconJobSummary | null;
  policy_warnings?: Array<{ code: string; severity: string; message: string }>;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  /** When the worker actually picked the job up. Null while still in
   *  the queue. Used together with updated_at to compute total runtime. */
  started_at?: string | null;
}

export interface IsinBreakdown {
  /** Portfolio-side roll-up across all txns for this ISIN. */
  portfolio: {
    buys_qty: number;          // sum of +qty rows (BUY/BUYCR/BONUS/SPLIT BUY/DEMERGERIN/DEMERGERBUY)
    buys_count: number;
    sells_qty: number;         // sum of -qty rows, negative
    sells_count: number;
    dividends_count: number;
    dividends_amount: number;
    corporate_action_count: number;
    other_count: number;
  };
  /** DP-side roll-up. credits_qty − debits_qty = dp_net. */
  dp: {
    credits_qty: number;
    credits_count: number;
    debits_qty: number;        // positive number
    debits_count: number;
  };
}

export interface ReconRow {
  id: string;
  canonical_isin: string;
  display_name: string;
  /** Status values:
   *   holdings_v1:     'MATCH' | 'QTY_MISMATCH' | 'ONLY_IN_PORTFOLIO' | 'ONLY_IN_DP' | 'ISIN_UNRESOLVABLE'
   *   transactions_v1: 'MATCH' | 'PARTIAL_MATCH' | 'TXN_ALL_UNMATCHED' |
   *                    'ONLY_IN_PORTFOLIO' | 'ONLY_IN_DP' | 'TXN_OUTSIDE_WINDOW' |
   *                    'SLB_OUTSTANDING'
   *  Union of both for type-safety; UI branches on the job's
   *  format_version when interpreting unfamiliar codes.
   *
   *  SLB_OUTSTANDING: dp_net + outstanding SLB loans == portfolio_net.
   *  Real economic state (shares lent via Securities Lending & Borrowing,
   *  still beneficially owned), not a reconciliation error. Shown
   *  separately from QTY_MISMATCH so reviewers can see at a glance. */
  status:
    | 'MATCH' | 'QTY_MISMATCH' | 'ONLY_IN_PORTFOLIO' | 'ONLY_IN_DP' | 'ISIN_UNRESOLVABLE'
    | 'PARTIAL_MATCH' | 'TXN_ALL_UNMATCHED' | 'TXN_OUTSIDE_WINDOW'
    | 'SLB_OUTSTANDING';
  portfolio_isin: string | null;
  portfolio_qty: number;
  dp_isin: string | null;
  dp_qty: number;
  qty_diff: number;
  /** DP closing-balance breakdown. free + pledge + demat = dp_closing_balance.
   *  All null on ONLY_IN_PORTFOLIO rows. */
  dp_opening_balance: number | null;
  dp_closing_balance: number | null;
  dp_free_balance: number | null;
  dp_pledge_balance: number | null;
  dp_demat_balance: number | null;
  /** Transaction-mode counts. Populated when the parent job's
   *  format_version === 'transactions_v1'; default 0 for holdings_v1. */
  portfolio_txn_count?: number;
  dp_txn_count?: number;
  matched_txn_count?: number;
  unmatched_pf_count?: number;
  unmatched_dp_count?: number;
  outside_window_count?: number;
  /** 1-indexed source page in the DP PDF where this ISIN section begins.
   *  Used by the verify page to jump PDF.js to the correct page when
   *  the operator clicks an ISIN. For multi-sub-section ISINs (e.g.
   *  Tata Elxsi with Beneficiary + Pledge + Unconfirmed Pledge), this
   *  is the EARLIEST page across all sub-sections. Null for
   *  ONLY_IN_PORTFOLIO rows (no DP page) and for any row persisted
   *  before migration 012. Frontend handles null by falling back to
   *  the older /dp_section_index endpoint, then to page 1. */
  dp_start_page?: number | null;
  /** Per-side activity breakdown for the drill-down explanation panel.
   *  Derived fresh from txn_pairs by the API on read (not persisted on
   *  recon_row). Present for transactions_v1 jobs; undefined for older
   *  holdings_v1 rows. */
  breakdown?: IsinBreakdown;
  root_cause_code: string | null;
  confidence: number | null;
  classification: {
    reasoning: string;
    evidence: Array<{ source: string; detail: string }>;
    proposed_resolution_summary: string;
    requires_human_approval: boolean;
    human_review_checklist: string[];
  } | null;
  resolution_state:
    | 'pending_maker'
    | 'pending_checker'
    | 'approved'
    | 'rejected_by_checker'
    | 'rejected_by_maker'
    | 'applied';
  // Maker step
  proposed_action: 'accept_proposal' | 'override' | 'reject' | null;
  proposed_root_cause_code: string | null;
  proposed_resolution_note: string | null;
  proposed_by: string | null;
  proposed_at: string | null;
  // Checker step
  checker_decision: 'approved' | 'rejected' | null;
  checker_note: string | null;
  checked_by: string | null;
  checked_at: string | null;
  // Apply
  applied_at: string | null;
  applied_by: string | null;
}

/** One individual transaction in a transactions_v1 reconciliation. */
export interface ReconTxnPair {
  id: string;
  row_id: string;
  canonical_isin: string;
  status: 'PAIRED' | 'UNMATCHED_PORTFOLIO' | 'UNMATCHED_DP' | 'OUTSIDE_WINDOW';
  // Portfolio side (null on UNMATCHED_DP)
  pf_txn_date: string | null;
  pf_txn_type: string | null;
  pf_qty: number | null;
  pf_trade_price: number | null;
  pf_charges: number | null;
  pf_trade_value: number | null;
  pf_source: string | null;
  /** 1-indexed Excel source-row for cross-reference. Null for
   *  pre-migration-013 jobs and for DP-only pairs. */
  pf_source_row: number | null;
  /** Settlement code from the Particulars column of the PF Excel
   *  (e.g. "2023142"). Null for pre-migration-015 jobs or when
   *  the column was absent/empty. */
  pf_particulars: string | null;
  // DP side (null on UNMATCHED_PORTFOLIO and OUTSIDE_WINDOW)
  dp_txn_date: string | null;
  dp_description: string | null;
  dp_credit: number | null;
  dp_debit: number | null;
  dp_balance: number | null;
  /** 1-indexed source row in the CSV/Excel DP file. Null for PDF
   *  DP files (page-jumping is via row.dp_start_page instead), for
   *  portfolio-only pairs, and for pre-migration-014 jobs. */
  dp_source_row: number | null;
  /** True when matched under Tier-3 bracketed pledge-recovery.
   *  A normally-hidden NUT2PU+47 row was admitted via the exception list. */
  is_tier3_match: boolean;
  /** Frontend-only: when a company's multiple ISINs are combined into one
   *  matching window, canonical_isin is unified for matching while this holds
   *  the row's original ISIN for the per-row ISIN display. */
  _displayIsin?: string;
  // Classifier output (inherited from the parent row's ISIN batch
  // classification for non-PAIRED pairs; null on PAIRED)
  root_cause_code: string | null;
  confidence: number | null;
  classification: ReconRow['classification'];
}

export const reconApi = {
  list: () => api.get<{ rows: ReconJob[] }>('/api/recon/jobs').then(r => r.data),
  get: (id: string) =>
    api.get<ReconJob & { rows: ReconRow[]; txn_pairs?: ReconTxnPair[] }>(`/api/recon/jobs/${id}`).then(r => r.data),
  create: (args: {
    portfolio_file: File;
    pf_holding_file?: File | null;
    dp_holding_file?: File | null;
    portfolio_files?: File[];
    dp_files: File[];
    /** Optional. Empty string or missing key is fine; backend reads the
     *  as-on date from the Excel metadata if not supplied. */
    portfolio_as_on?: string;
    /** Optional. Empty string or missing key is fine; backend reads
     *  the statement_from / statement_to from the DP PDF if not supplied. */
    dp_as_on?: string;
  }) => {
    const form = new FormData();
    form.append('portfolio_file', args.portfolio_file);
    if (args.portfolio_files && args.portfolio_files.length > 1) {
      args.portfolio_files.slice(1).forEach(f => form.append('portfolio_files', f));
    }
    args.dp_files.forEach(f => form.append('dp_files', f));
    if (args.pf_holding_file) form.append('pf_holding_file', args.pf_holding_file);
    if (args.dp_holding_file) form.append('dp_holding_file', args.dp_holding_file);
    // Only append the date fields when they have a value. The backend's
    // Zod schema preprocessor treats empty string as undefined, but
    // omitting the field entirely is cleaner and matches the contract.
    if (args.portfolio_as_on) form.append('portfolio_as_on', args.portfolio_as_on);
    if (args.dp_as_on)        form.append('dp_as_on', args.dp_as_on);
    return api.post<{ job_id: string; status: string }>('/api/recon/jobs', form).then(r => r.data);
  },
  propose: (rowId: string, body: {
    proposed_action: 'accept_proposal' | 'override' | 'reject';
    proposed_root_cause_code?: string;
    note?: string;
  }) => api.post(`/api/recon/rows/${rowId}/propose`, body).then(r => r.data),
  approve: (rowId: string, body: {
    checker_decision: 'approved' | 'rejected';
    note?: string;
  }) => api.post(`/api/recon/rows/${rowId}/approve`, body).then(r => r.data),
  apply: (rowId: string) =>
    api.post(`/api/recon/rows/${rowId}/apply`, {}).then(r => r.data),

  /** Cancel an in-flight reconciliation job. Aborts any open Claude
   *  streams and marks the row failed atomically. Idempotent on
   *  already-terminal rows. */
  cancel: (job_id: string, reason?: string) =>
    api.post<{
      ok: true;
      cancelled?: boolean;
      already_terminal?: boolean;
      status?: string;
      prev_status?: string;
      worker_signalled?: boolean;
    }>(`/api/recon/jobs/${job_id}/cancel`, { reason }).then(r => r.data),

  /** Rerun-mismatches — re-extracts DP statements and updates only the
   *  rows that were not MATCH in the previous run. MATCH rows survive
   *  untouched. Backend enqueues a worker job and returns 202 with the
   *  scope summary. UI should poll /api/recon/jobs/:id for status. */
  rerunMismatches: (job_id: string) =>
    api.post<{
      ok: true;
      job_id: string;
      status: 'queued';
      mismatch_count: number;
      preserved_match_count: number;
    }>(`/api/recon/jobs/${job_id}/rerun-mismatches`, {}).then(r => r.data),

  /** Verify-page support — fetch the per-DP-file ISIN section index,
   *  used to jump PDF.js to the right page when an ISIN is clicked.
   *  Source: indexer's dp.index.payload progress events (Redis history,
   *  24h TTL). Older jobs return an empty list; frontend falls back to
   *  "page 1, use the page navigator". */
  sectionIndex: (job_id: string) =>
    api.get<{
      files: Array<{
        filename: string;
        dp_index: number;
        sections: Array<{
          isin: string;
          security_name: string;
          start_page: number;
          closing_balance: number | null;
        }>;
      }>;
      from_redis_history: boolean;
    }>(`/api/recon/jobs/${job_id}/dp_section_index`).then(r => r.data),

  /** Get a presigned S3 URL for downloading a job file. kind is either
   *  'portfolio' or 'dp_N' where N is the index into dp_s3_keys. URL
   *  expires in 5 minutes. PDF.js fetches the binary from this URL. */
  fileUrl: (job_id: string, kind: 'portfolio' | `dp_${number}`) =>
    api.get<{ url: string; expires_in: number }>(`/api/recon/jobs/${job_id}/file/${kind}`).then(r => r.data),

  /** Re-reads the stored DP CSV/Excel file and patches dp_client_id in the
   *  job summary. Safe to call on completed jobs — no rerun needed. */
  backfillDpClientId: (job_id: string) =>
    api.post<{ dp_client_id: string | null }>(`/api/recon/jobs/${job_id}/backfill-dp-client-id`, {}).then(r => r.data),

  /** Recompute portfolio_qty / dp_qty / qty_diff / status for a single
   *  recon_row from its already-stored recon_txn_pair rows.
   *  Called automatically by the frontend whenever effectiveStatusByRow
   *  disagrees with the DB-stored row.status (stale engine output).
   *  Returns the new values so the caller can decide whether to invalidate
   *  the React Query cache. */
  recomputeRow: (job_id: string, row_id: string) =>
    api.post<{
      row_id: string;
      old_status: string;
      new_status: string;
      portfolio_qty: number;
      dp_qty: number;
      qty_diff: number;
      changed: boolean;
    }>(`/api/recon/jobs/${job_id}/rows/${row_id}/recompute`, {}).then(r => r.data),
  saveLiveStats: (job_id: string, stats: { matched: number; mismatches: number; total: number; matchRatePct: number }) =>
    api.post<{ ok: boolean; stored: boolean }>(`/api/recon/jobs/${job_id}/live-stats`, stats).then(r => r.data),
  delete: (job_id: string) =>
    api.delete(`/api/recon/jobs/${job_id}`).then(r => r.data),

  triggerHoldingRecon: (job_id: string) =>
    api.post<{ holding_job_id: string }>(`/api/recon/jobs/${job_id}/holding-recon`).then(r => r.data),
};

// ─── Holding Recon API ────────────────────────────────────────────────────────

export interface HoldingReconJob {
  id: string;
  status: 'queued' | 'processing' | 'awaiting_review' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  error_message?: string | null;
  summary?: {
    pf_client_id?: string | null;
    dp_client_id?: string | null;
    match_rate_pct?: number | null;
    mismatch_count?: number | null;
  } | null;
  rows?: any[];
}

export const holdingReconApi = {
  list: () =>
    api.get<{ rows: HoldingReconJob[] }>('/api/holding-recon/jobs').then(r => r.data),

  get: (id: string) =>
    api.get<HoldingReconJob>(`/api/holding-recon/jobs/${id}`).then(r => r.data),

  create: (args: { pf_file: File; dp_file: File }) => {
    const fd = new FormData();
    fd.append('pf_file', args.pf_file);
    fd.append('dp_file', args.dp_file);
    return api
      .post<{ job_id: string }>('/api/holding-recon/jobs', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data);
  },

  cancel: (id: string) =>
    api.post(`/api/holding-recon/jobs/${id}/cancel`).then(r => r.data),

  delete: (id: string) =>
    api.delete(`/api/holding-recon/jobs/${id}`).then(r => r.data),
};

// ─── F&O Recon API ────────────────────────────────────────────────────────────

export interface FnoSegmentSummary {
  segment: 'OPTIONS' | 'FUTURES';
  pf_count: number; bo_count: number;
  matched: number; pf_only: number; bo_only: number;
  match_rate_pct: number;
  pf_qty_total: number; bo_qty_total: number;
  contract_count: number;
}
export interface FnoMatchRow {
  segment: 'OPTIONS' | 'FUTURES';
  status: 'MATCHED' | 'PF_ONLY' | 'BO_ONLY';
  contract_id: string;
  trade_date: string; buy_sell: 'BUY' | 'SELL';
  pf_type?: string | null;
  pf_qty: number | null; pf_price: number | null; pf_value: number | null; pf_row: number | null;
  bo_qty: number | null; bo_price: number | null; bo_amount: number | null; bo_row: number | null;
  bo_contract: string | null;
  worthless_expiry?: boolean;
  corporate_action?: boolean;
  bo_date?: string | null;
  waiting_for_entries?: boolean;
}
export interface FnoReconJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  error_message?: string | null;
  summary?: { segments: FnoSegmentSummary[]; overall: { pf_count: number; bo_count: number; matched: number; pf_only: number; bo_only: number; match_rate_pct: number; pf_client_id: string | null; bo_client_id: string | null; contract_count: number; pf_qty_total: number; bo_qty_total: number; mismatch_count: number } } | null;
  rows?: FnoMatchRow[];
}

export const fnoReconApi = {
  list: () =>
    api.get<{ rows: FnoReconJob[] }>('/api/fno-recon/jobs').then(r => r.data),

  get: (id: string) =>
    api.get<FnoReconJob>(`/api/fno-recon/jobs/${id}`).then(r => r.data),

  recompute: (id: string) =>
    api.post<{ job_id: string; recomputed: boolean; matched: number }>(`/api/fno-recon/jobs/${id}/recompute`).then(r => r.data),

  create: (args: { portfolio_files: File[]; dp_files: File[] }) => {
    const form = new FormData();
    args.portfolio_files.forEach(f => form.append('portfolio_files', f));
    args.dp_files.forEach(f => form.append('dp_files', f));
    return api
      .post<{ job_id: string }>('/api/fno-recon/jobs', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data);
  },

  delete: (id: string) =>
    api.delete(`/api/fno-recon/jobs/${id}`).then(r => r.data),
};
